import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { MessageSquare, Send, LogOut, Shield, Lock, Search, Plus, User, Menu, X, Users, Phone, Copy, Check, Trash2, Loader2, Mail, Link, UserPlus, Share2 } from 'lucide-react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import SecurityPrompt from '@/components/SecurityPrompt';
import { ConversationListSkeleton } from '@/components/Skeletons';
import { initChatCache, getCachedConversations, cacheConversations } from '@/utils/chatCache';
import { useClickLock } from '../hooks';
import { trackInviteSent } from '@/utils/analytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [conversations, setConversations] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);  // For skeleton loader
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contactPickerSupported, setContactPickerSupported] = useState(false);
  const [showSecurityPrompt, setShowSecurityPrompt] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);  // Total unread messages
  const [incomingCall, setIncomingCall] = useState(null);  // Incoming call data for popup
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);  // Conversation ID to delete
  const [friendRequests, setFriendRequests] = useState([]);  // Pending friend requests
  const [friendRequestsLoading, setFriendRequestsLoading] = useState(false);
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const ringtoneRef = useRef(null);

  // Click lock for critical actions
  const { loading: sendLoading, execute: executeSend } = useClickLock();
  const { loading: deleteLoading, execute: executeDelete } = useClickLock();
  const { loading: logoutLoading, execute: executeLogout } = useClickLock();

  // Send message form (for SMS/Email notification)
  const [toPhone, setToPhone] = useState('');
  const [message, setMessage] = useState('');
  const [channel, setChannel] = useState('sms');  // Default to SMS tab
  const [toEmail, setToEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [senderContact, setSenderContact] = useState('');

  // Check if Contact Picker API is supported
  useEffect(() => {
    const checkContactPickerSupport = async () => {
      try {
        if ('contacts' in navigator && 'ContactsManager' in window) {
          const supportedProps = await navigator.contacts.getProperties();
          if (supportedProps && supportedProps.length > 0) {
            setContactPickerSupported(true);
          }
        }
      } catch (e) {
        console.log('Contact Picker not supported:', e.message);
        setContactPickerSupported(false);
      }
    };
    checkContactPickerSupport();
  }, []);

  // Socket.IO connection for real-time updates
  useEffect(() => {
    if (!socketRef.current && user) {
      const { io } = require('socket.io-client');
      socketRef.current = io(process.env.REACT_APP_BACKEND_URL, {
        path: '/api/socket.io',
        transports: ['websocket', 'polling'],
        auth: {
          user_id: String(user.id)  // Pass user_id in auth
        }
      });
      
      socketRef.current.on('connect', () => {
        console.log('[Dashboard] Socket connected:', socketRef.current.id);
        socketRef.current.emit('register_user', { user_id: user.id });
        
        // Join all conversation rooms to receive real-time messages
        conversations.forEach(conv => {
          socketRef.current.emit('join', { conversation_id: conv.id });
        });
      });
      
      // Handle conversation updates (new messages)
      socketRef.current.on('conversation_updated', (data) => {
        console.log('Conversation updated:', data);
        
        setConversations(prev => {
          // Check if conversation exists in the list
          const existingIndex = prev.findIndex(conv => conv.id === data.conversation_id);
          
          if (existingIndex >= 0) {
            // Update existing conversation
            const updated = prev.map(conv => {
              if (conv.id === data.conversation_id) {
                return {
                  ...conv,
                  last_message: data.last_message,
                  updated_at: data.updated_at,
                  unread_count: (conv.unread_count || 0) + 1
                };
              }
              return conv;
            });
            // Sort by updated_at (most recent first)
            return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
          } else if (data.conversation_restored) {
            // Conversation was hidden but now restored - fetch full conversation list
            // This happens when someone you "deleted" sends you a new message
            console.log('Conversation restored, refreshing list...');
            fetchConversations();
            return prev;
          }
          return prev;
        });
        
        // Update total unread count
        setTotalUnread(prev => prev + 1);
        
        // Show toast notification for new message
        toast.info(`New message from ${data.from_user_name}`, {
          description: data.last_message?.slice(0, 50),
          action: {
            label: 'View',
            onClick: () => navigate(`/chat/${data.conversation_id}`)
          }
        });
      });
      
      // Handle user status changes
      socketRef.current.on('user_status_change', (data) => {
        setConversations(prev => prev.map(conv => {
          // Check if this status change is for the other user in this conversation
          const otherUserId = conv.user1_id === user?.id ? conv.user2_id : conv.user1_id;
          if (otherUserId === data.user_id) {
            return {
              ...conv,
              other_user_online: data.is_online,
              other_user_last_seen: data.last_seen
            };
          }
          return conv;
        }));
      });
      
      // Handle incoming calls when user is on dashboard (not in conversation)
      socketRef.current.on('incoming_call', (data) => {
        console.log('[Dashboard] Incoming call:', data);
        // Set incoming call to show popup
        setIncomingCall(data);
        
        // Vibrate if supported
        if (navigator.vibrate) {
          // Vibrate pattern: vibrate for 500ms, pause 200ms, repeat
          navigator.vibrate([500, 200, 500, 200, 500]);
        }
      });
      
      // Handle call ended/rejected while on dashboard
      socketRef.current.on('call_ended', () => {
        console.log('[Dashboard] Call ended');
        setIncomingCall(null);
      });
      
      socketRef.current.on('call_rejected', () => {
        console.log('[Dashboard] Call rejected');
        setIncomingCall(null);
      });
      
      // Also listen for new_message to handle real-time updates across all conversations
      socketRef.current.on('new_message', (message) => {
        console.log('[Dashboard] New message received:', message);
        
        // If this message is TO us, emit message_delivered
        if (message.to_user_id === user?.id && message.id) {
          console.log('[Dashboard] Emitting message_delivered for:', message.id);
          socketRef.current.emit('message_delivered', {
            message_id: message.id,
            sender_id: message.from_user_id
          });
        }
        
        // Update the conversation list with the new message
        setConversations(prev => {
          const updated = prev.map(conv => {
            if (conv.id === message.conversation_id) {
              // Only increment unread if message is from other user
              const isFromOther = message.from_user_id !== user?.id;
              return {
                ...conv,
                last_message: message.message || (message.message_type === 'image' ? '📷 Photo' : 
                               message.message_type === 'video' ? '🎬 Video' : 
                               message.message_type === 'audio' ? '🎤 Voice note' : 'New message'),
                updated_at: message.created_at,
                unread_count: isFromOther ? (conv.unread_count || 0) + 1 : conv.unread_count
              };
            }
            return conv;
          });
          return updated.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        });
      });
    }
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [user, navigate]);

  // Cleanup socket on page unload (browser close/refresh/logout)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (socketRef.current) {
        socketRef.current.emit('logout');
        socketRef.current.disconnect();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Join conversation rooms when conversations are loaded
  useEffect(() => {
    if (socketRef.current && socketRef.current.connected && conversations.length > 0) {
      console.log('Joining', conversations.length, 'conversation rooms');
      conversations.forEach(conv => {
        socketRef.current.emit('join', { conversation_id: conv.id });
      });
    }
  }, [conversations]);

  // Accept incoming call
  const acceptIncomingCall = () => {
    if (incomingCall) {
      // Stop vibration
      if (navigator.vibrate) {
        navigator.vibrate(0);
      }
      // Navigate to conversation with incoming call data
      navigate(`/chat/${incomingCall.conversation_id}`, { 
        state: { incomingCall } 
      });
      setIncomingCall(null);
    }
  };

  // Decline incoming call
  const declineIncomingCall = () => {
    if (incomingCall && socketRef.current) {
      // Stop vibration
      if (navigator.vibrate) {
        navigator.vibrate(0);
      }
      // Notify caller that call was rejected
      socketRef.current.emit('reject_call', {
        caller_id: incomingCall.caller_id,
        call_id: incomingCall.call_id
      });
      setIncomingCall(null);
      toast.info('Call declined');
    }
  };

  // Pick contact from phone
  const pickContact = async () => {
    try {
      if (!('contacts' in navigator)) {
        toast.error('Contact picker is not supported on this browser. Try Chrome on Android.');
        return;
      }
      
      const props = ['tel', 'name'];
      const opts = { multiple: false };
      const contacts = await navigator.contacts.select(props, opts);
      
      if (contacts && contacts.length > 0) {
        const contact = contacts[0];
        if (contact.tel && contact.tel.length > 0) {
          let phone = contact.tel[0];
          phone = phone.replace(/[\s\-\(\)]/g, '');
          if (!phone.startsWith('+')) {
            if (phone.length === 10) {
              phone = '+91' + phone;
            } else {
              phone = '+' + phone;
            }
          }
          setToPhone(phone);
          if (contact.name && contact.name.length > 0) {
            toast.success(`Selected: ${contact.name[0]}`);
          }
        }
      }
    } catch (error) {
      console.error('Contact picker error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Permission denied to access contacts');
      } else if (error.name !== 'TypeError') {
        toast.error('Contact picker not available on this device');
      }
    }
  };

  useEffect(() => {
    fetchUser();
    fetchConversations();
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
      
      // Check if profile is complete - only username and agreed_to_monitoring are required before entering
      const hasUsername = response.data.username && response.data.username.trim() !== '';
      const agreedToMonitoring = response.data.agreed_to_monitoring;
      
      if (!agreedToMonitoring || !hasUsername) {
        navigate('/complete-profile');
        return;
      }
      
      // Check if we should show security prompt (missing email, phone, or password)
      const hasEmail = response.data.email && response.data.email.trim() !== '';
      const hasPhone = response.data.phone && response.data.phone.trim() !== '';
      const hasPassword = response.data.has_password;
      
      // Show security prompt if any security feature is missing
      // But respect if user dismissed it recently (within 24 hours)
      const lastDismissed = localStorage.getItem('securityPromptDismissed');
      const dismissedRecently = lastDismissed && (Date.now() - parseInt(lastDismissed)) < 24 * 60 * 60 * 1000;
      
      if ((!hasEmail || !hasPhone || !hasPassword) && !dismissedRecently) {
        // Delay showing the prompt so user sees the dashboard first
        setTimeout(() => setShowSecurityPrompt(true), 2000);
      }
    } catch (error) {
      // Only logout on actual auth errors (401 Unauthorized)
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        console.error('Failed to fetch user data:', error);
        // Don't logout on network errors or other issues - let user retry
        toast.error('Failed to load profile. Please refresh the page.');
      }
    }
  };

  // Use lightweight endpoint for faster initial load (background sync)
  const fetchConversations = async (silent = false) => {
    try {
      const token = localStorage.getItem('token');
      
      // PERFORMANCE: Show cached conversations instantly
      if (!silent) {
        try {
          await initChatCache();
          const cached = await getCachedConversations();
          if (cached?.length > 0) {
            setConversations(cached);
            setTotalUnread(cached.reduce((sum, conv) => sum + (conv.unread_count || 0), 0));
            setInitialLoading(false);  // Hide skeleton immediately
            console.log('[Dashboard] Showing cached conversations instantly');
          }
        } catch (e) {
          console.log('[Dashboard] Cache not available');
        }
      }
      
      // Fetch fresh data in background
      const response = await axios.get(`${API}/messages/conversations/light`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
      setInitialLoading(false);
      
      // Calculate total unread count
      const unread = response.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setTotalUnread(unread);
      
      // PERFORMANCE: Cache conversations in background
      cacheConversations(response.data).catch(() => {});
    } catch (error) {
      setInitialLoading(false);
      // Only show error if not a silent refresh
      if (!silent) {
        console.error('Failed to fetch conversations', error);
      }
    }
  };
  
  // Background sync - refresh every 30 seconds silently
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations(true);  // Silent refresh
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch pending friend requests
  const fetchFriendRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/friend-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFriendRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch friend requests', error);
    }
  };

  // Accept friend request
  const acceptFriendRequest = async (requestId) => {
    setFriendRequestsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/friend-requests/${requestId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Friend request accepted!');
      // Remove from local state
      setFriendRequests(prev => prev.filter(r => r.id !== requestId));
      // Navigate to the new conversation
      if (response.data.conversation_id) {
        navigate(`/chat/${response.data.conversation_id}`);
      }
    } catch (error) {
      toast.error('Failed to accept friend request');
    } finally {
      setFriendRequestsLoading(false);
    }
  };

  // Decline friend request
  const declineFriendRequest = async (requestId) => {
    setFriendRequestsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/friend-requests/${requestId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state silently
      setFriendRequests(prev => prev.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error('Failed to decline request');
    } finally {
      setFriendRequestsLoading(false);
    }
  };

  // Fetch friend requests on mount
  useEffect(() => {
    fetchFriendRequests();
  }, []);

  // Send message via SMS or Email - uses unified invites endpoint
  const sendMessage = async () => {
    if (channel === 'email') {
      if (!toEmail || !message) {
        toast.error('Please fill email and message');
        return;
      }
    } else {
      if (!toPhone || !message) {
        toast.error('Please fill phone and message');
        return;
      }
    }

    executeSend(async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const sourceButton = channel === 'sms' ? 'dialog_sms' : 'dialog_email';
        await axios.post(
          `${API}/invite/send`,
          {
            channel: channel,
            recipient_phone: toPhone || null,
            recipient_email: toEmail || null,
            message: message,
            sender_name: senderName || null,
            source_button: sourceButton
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Track invite sent event in Meta Pixel
        trackInviteSent(channel, sourceButton);
        toast.success('Invite sent successfully!');
        setShowSendDialog(false);
        setToPhone('');
        setMessage('');
        setToEmail('');
        setSenderName('');
        setSenderContact('');
        setChannel('sms');
        fetchConversations();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to send invite');
      } finally {
        setLoading(false);
      }
    });
  };

  // Generate invite link preview (actual link is generated by backend)
  const getInviteMessage = () => {
    const username = user?.username?.toLowerCase() || 'friend';
    const displayName = senderName || user?.username || 'Someone';
    return `${displayName} invited you to Privluxe

privluxe.com/j/${username}/...`;
  };

  // Copy/Share invite - creates invite in backend first to get proper token
  const copyInvite = async () => {
    try {
      // First create the invite in backend to get the token
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/invite/create`, {
        method: 'link',
        message: message,
        sender_name: senderName || null,
        source_button: 'dialog_link'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const inviteUrl = response.data.invite_url;
      const displayName = senderName || user?.username || 'Someone';
      
      // Shorter, cleaner message
      const fullMessage = `${displayName} invited you to Privluxe\n\n${inviteUrl}`;

      // Detect mobile devices - prioritize Web Share API on mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // On mobile: Use Web Share API first (opens native share sheet → WhatsApp directly)
      // This is more reliable than clipboard on iOS Safari
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: 'Privluxe Invite',
            text: fullMessage,
          });
          // Track invite sent event in Meta Pixel
          trackInviteSent('link', 'dialog_link');
          setInviteCopied(true);
          toast.success('Shared successfully!');
          setTimeout(() => setInviteCopied(false), 3000);
          return; // Exit early on successful share
        } catch (shareError) {
          // User cancelled or share failed - fall through to clipboard
          if (shareError.name !== 'AbortError') {
            console.log('Share API failed, trying clipboard:', shareError);
          } else {
            // User cancelled the share dialog
            return;
          }
        }
      }
      
      // Desktop or Share API failed: Use clipboard
      let copied = false;
      
      // Method 1: Modern Clipboard API
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        try {
          await navigator.clipboard.writeText(fullMessage);
          copied = true;
        } catch (e) {
          console.log('Clipboard API failed:', e);
        }
      }
      
      // Method 2: Fallback for older browsers
      if (!copied) {
        try {
          const textArea = document.createElement('textarea');
          textArea.value = fullMessage;
          textArea.style.cssText = 'position:fixed;left:0;top:0;width:1px;height:1px;padding:0;border:none;outline:none;boxShadow:none;background:transparent;opacity:0;';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          textArea.setSelectionRange(0, fullMessage.length);
          copied = document.execCommand('copy');
          document.body.removeChild(textArea);
        } catch (e2) {
          console.log('execCommand fallback failed:', e2);
          copied = false;
        }
      }
      
      if (copied) {
        setInviteCopied(true);
        // Track invite sent event in Meta Pixel
        trackInviteSent('link', 'dialog_link');
        toast.success('Invite copied! Paste in WhatsApp');
        setTimeout(() => setInviteCopied(false), 3000);
      } else {
        // Nothing worked - show manual instruction
        toast.error('Tap and hold to copy: ' + inviteUrl);
      }
    } catch (error) {
      console.error('Failed to create invite:', error);
      toast.error('Failed to create invite link');
    }
  };

  const logout = () => {
    executeLogout(async () => {
      // Disconnect socket before logging out
      if (socketRef.current) {
        socketRef.current.emit('logout');  // Notify server
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Also call logout API to ensure server-side cleanup
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await axios.post(`${API}/auth/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      } catch (e) {
        // Ignore errors - user is logging out anyway
      }
      
      localStorage.removeItem('token');
      navigate('/');
    });
  };

  const getOtherUser = (conv) => {
    if (!user) return {};
    // Support both old format (user1_name/user2_name) and new lightweight format (other_user_name)
    if (conv.other_user_name !== undefined) {
      return { 
        name: conv.other_user_name, 
        phone: conv.other_user_phone, 
        id: conv.other_user_id 
      };
    }
    // Fallback to old format
    if (conv.user1_id === user.id) {
      return { name: conv.user2_name || conv.user2_phone, phone: conv.user2_phone, id: conv.user2_id };
    }
    return { name: conv.user1_name || conv.user1_phone, phone: conv.user1_phone, id: conv.user1_id };
  };

  const filteredConversations = conversations.filter(conv => {
    const otherUser = getOtherUser(conv);
    return otherUser.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           otherUser.phone?.includes(searchQuery);
  });

  const handleConversationClick = (conv) => {
    setSidebarOpen(false);
    // Pass conversation data to avoid "User" flash while loading
    navigate(`/chat/${conv.id}`, { 
      state: { 
        preloadedConversation: {
          id: conv.id,
          user1_id: conv.user1_id,
          user2_id: conv.user2_id,
          user1_name: conv.user1_name,
          user2_name: conv.other_user_name || conv.user2_name,
          user1_phone: conv.user1_phone,
          user2_phone: conv.user2_phone,
          other_user_online: conv.other_user_online,
          other_user_last_seen: conv.other_user_last_seen
        }
      }
    });
  };

  // Delete conversation
  const handleDeleteConversation = (conversationId, e) => {
    if (e) e.stopPropagation();
    
    executeDelete(async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API}/messages/conversation/${conversationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Remove from local state
        setConversations(prev => prev.filter(c => c.id !== conversationId));
        toast.success('Conversation deleted');
        setShowDeleteConfirm(null);
      } catch (error) {
        console.error('Delete conversation error:', error);
        toast.error('Failed to delete conversation');
      }
    });
  };

  return (
    <div 
      className="flex" 
      style={{ 
        background: '#020617',
        minHeight: '100vh',
        minHeight: '100dvh'
      }} 
      data-testid="dashboard"
    >
      {/* Mobile Header */}
      <div 
        className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-3 border-b safe-area-top"
        style={{ 
          background: 'rgba(10, 15, 44, 0.95)',
          backdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        <button 
          onClick={() => setSidebarOpen(true)}
          className="p-2 -ml-2 text-gray-400 hover:text-white"
        >
          <Menu className="h-6 w-6" />
        </button>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
          <span className="font-bold tracking-[0.1em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
        </div>
        <Button 
          onClick={logout} 
          variant="ghost" 
          size="sm"
          className="p-2 -mr-2 text-gray-400 hover:text-white hover:bg-transparent"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <div 
          className="md:hidden fixed inset-0 z-50 bg-black/60"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed md:relative z-50 md:z-auto
          w-[85%] max-w-[320px] md:w-80 h-full
          flex flex-col border-r
          transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        `}
        style={{ 
          background: 'rgba(10, 15, 44, 0.98)',
          borderColor: 'rgba(255, 255, 255, 0.05)'
        }}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b safe-area-top" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" style={{ color: '#7C3AED' }} />
              <span className="font-bold tracking-[0.15em]" style={{ color: '#E5E7EB' }}>PRIVLUXE</span>
            </div>
            <div className="flex items-center gap-1">
              <Button 
                onClick={() => setSidebarOpen(false)}
                variant="ghost" 
                size="sm"
                className="md:hidden text-gray-400 hover:text-white hover:bg-white/10"
              >
                <X className="h-5 w-5" />
              </Button>
              <Button 
                onClick={logout} 
                variant="ghost" 
                size="sm"
                className="hidden md:flex text-gray-400 hover:text-white hover:bg-white/10"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* User info */}
          {user && (
            <div 
              className="flex items-center gap-3 p-3 rounded-lg mb-3"
              style={{ background: 'rgba(255, 255, 255, 0.03)' }}
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
              >
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm truncate" style={{ color: '#E5E7EB' }}>
                  {user.username || 'User'}
                </p>
                <p className="text-xs truncate" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                  {user.phone || user.email}
                </p>
              </div>
            </div>
          )}
          
          {/* Debug logs link - only for Nitz03 */}
          {user?.username?.toLowerCase() === 'nitz03' && (
            <button
              onClick={() => { navigate('/debug-logs'); setSidebarOpen(false); }}
              className="text-xs text-gray-500 hover:text-purple-400 mb-3 transition-colors"
            >
              🔧 View Debug Logs
            </button>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: 'rgba(229, 231, 235, 0.3)' }} />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus:border-purple-500"
            />
          </div>
          
          {/* Friend Requests Button - ALWAYS VISIBLE */}
          <div 
            className="mt-3 p-3 rounded-lg cursor-pointer transition-all hover:bg-white/10"
            style={{ background: 'rgba(124, 58, 237, 0.15)', border: '1px solid rgba(124, 58, 237, 0.3)' }}
            onClick={() => {
              const section = document.getElementById('friend-requests-section');
              if (section) section.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="friend-requests-badge-btn"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" style={{ color: '#7C3AED' }} />
                <span className="font-medium text-sm" style={{ color: '#E5E7EB' }}>
                  Friend Requests
                </span>
              </div>
              <div 
                className="px-2 py-0.5 rounded-full text-xs font-bold text-white"
                style={{ background: friendRequests.length > 0 ? 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' : 'rgba(100,100,100,0.5)' }}
              >
                {friendRequests.length}
              </div>
            </div>
          </div>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto p-2 no-scrollbar" data-testid="conversations-list">
          {/* Skeleton loader for initial load */}
          {initialLoading && conversations.length === 0 ? (
            <ConversationListSkeleton count={6} />
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-8 px-4">
              {/* Welcome onboarding for new users */}
              {!searchQuery ? (
                <div className="space-y-6">
                  {/* Welcome header */}
                  <div>
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(34, 211, 238, 0.2) 100%)' }}>
                      <MessageSquare className="h-8 w-8" style={{ color: '#7C3AED' }} />
                    </div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#E5E7EB' }}>
                      Welcome to Privluxe!
                    </h3>
                    <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                      Your private messaging journey starts here
                    </p>
                  </div>
                  
                  {/* Quick start guide */}
                  <div className="space-y-3 text-left max-w-xs mx-auto">
                    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(124, 58, 237, 0.1)' }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: '#7C3AED', color: 'white' }}>1</div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#E5E7EB' }}>Invite a friend</p>
                        <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>Share your private invite link</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(229,231,235,0.5)' }}>2</div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>They join & accept</p>
                        <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>Friend creates account & accepts invite</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(229,231,235,0.5)' }}>3</div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>Start chatting</p>
                        <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>End-to-end encrypted messaging</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <Button 
                    onClick={() => setShowSendDialog(true)}
                    className="text-white font-semibold h-12 px-8"
                    style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                    data-testid="invite-friend-empty-btn"
                  >
                    <UserPlus className="h-5 w-5 mr-2" /> Invite Your First Friend
                  </Button>
                  
                  <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
                    Privluxe is invite-only for your security
                  </p>
                </div>
              ) : (
                <>
                  <MessageSquare className="h-10 w-10 mx-auto mb-3" style={{ color: 'rgba(229, 231, 235, 0.2)' }} />
                  <p className="text-sm" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                    No conversations found
                  </p>
                </>
              )}
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const otherUser = getOtherUser(conv);
              const hasUnread = conv.unread_count > 0;
              const isGuestChat = conv.is_guest_chat || conv.other_user_type === 'guest';
              return (
                <div
                  key={conv.id}
                  className="p-3 rounded-lg cursor-pointer transition-all mb-1 hover:bg-white/5 active:bg-white/10 group"
                  onClick={() => handleConversationClick(conv)}
                  data-testid={`conversation-${conv.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="relative">
                      {conv.other_user_profile_picture ? (
                        <img 
                          src={conv.other_user_profile_picture} 
                          alt={otherUser.name}
                          className="w-10 h-10 rounded-full object-cover shrink-0"
                        />
                      ) : (
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: isGuestChat ? 'rgba(34, 211, 238, 0.2)' : 'rgba(124, 58, 237, 0.2)' }}
                        >
                          <span style={{ color: isGuestChat ? '#22D3EE' : '#7C3AED' }}>{otherUser.name?.charAt(0)?.toUpperCase() || '?'}</span>
                        </div>
                      )}
                      {/* Online indicator - green dot */}
                      {conv.other_user_online && (
                        <div 
                          className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-green-500 border-2" 
                          style={{ borderColor: 'rgba(10, 15, 44, 0.98)' }}
                          title="Online"
                        />
                      )}
                      {/* Unread indicator */}
                      {hasUnread && !conv.other_user_online && (
                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-purple-500 border-2" style={{ borderColor: 'rgba(10, 15, 44, 0.98)' }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium text-sm truncate ${hasUnread ? 'font-semibold' : ''}`} style={{ color: '#E5E7EB' }}>
                            {otherUser.name}
                          </p>
                          {isGuestChat && !conv.other_user_converted && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34, 211, 238, 0.2)', color: '#22D3EE' }}>
                              Guest
                            </span>
                          )}
                          {conv.other_user_converted && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22C55E' }}>
                              Joined
                            </span>
                          )}
                          {conv.other_user_online && (
                            <span className="text-[10px] text-green-400">online</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          {hasUnread && (
                            <span className="text-xs font-bold px-1.5 py-0.5 rounded-full bg-purple-500 text-white min-w-[20px] text-center">
                              {conv.unread_count > 9 ? '9+' : conv.unread_count}
                            </span>
                          )}
                          <span className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </span>
                          {/* Delete conversation button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowDeleteConfirm(conv.id);
                            }}
                            className="p-1 rounded-lg hover:bg-red-500/20 transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete conversation"
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </button>
                        </div>
                      </div>
                      <p className={`text-xs truncate ${hasUnread ? 'font-medium' : ''}`} style={{ color: hasUnread ? '#E5E7EB' : 'rgba(229, 231, 235, 0.5)' }}>
                        {conv.last_message}
                      </p>
                    </div>
                  </div>
                  
                  {/* Delete confirmation */}
                  {showDeleteConfirm === conv.id && (
                    <div 
                      className="mt-2 p-2 rounded-lg flex items-center justify-between"
                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="text-xs text-red-300">Delete this chat?</span>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(null); }}
                          className="px-2 py-1 text-xs rounded bg-white/10 text-gray-300 hover:bg-white/20"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={(e) => handleDeleteConversation(conv.id, e)}
                          className="px-2 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* New message button */}
        <div className="p-4 border-t space-y-2 safe-area-bottom" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
          {/* Friends button */}
          <Button 
            onClick={() => navigate('/contacts')}
            variant="outline"
            className="w-full h-10 border-white/10 text-gray-300 hover:text-white hover:bg-white/5"
          >
            <Users className="h-4 w-4 mr-2" /> Friends
          </Button>
          
          {/* Unified Invite Dialog - SMS, Email, Copy Link */}
          <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
            <DialogTrigger asChild>
              <Button 
                className="w-full text-white font-medium h-12"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                data-testid="invite-button"
              >
                <Plus className="h-4 w-4 mr-2" /> Invite Friend
              </Button>
            </DialogTrigger>
            <DialogContent 
              className="max-w-[calc(100%-2rem)] sm:max-w-md mx-auto border-0 max-h-[90vh] overflow-y-auto"
              style={{ 
                background: 'rgba(10, 15, 44, 0.98)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              data-testid="invite-dialog"
            >
              <DialogHeader>
                <DialogTitle style={{ color: '#E5E7EB' }}>Invite a Friend</DialogTitle>
                <DialogDescription style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                  Send an anonymous invite via SMS, Email, or copy link
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Invite Method Tabs - SMS first, Email, Copy Link */}
                <div className="flex rounded-lg overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <button
                    onClick={() => setChannel('sms')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'sms' ? 'text-white' : 'text-gray-400'}`}
                    style={{ background: channel === 'sms' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
                    data-testid="tab-sms"
                  >
                    <Phone className="h-4 w-4 mx-auto mb-1" />
                    SMS
                  </button>
                  <button
                    onClick={() => setChannel('email')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'email' ? 'text-white' : 'text-gray-400'}`}
                    style={{ background: channel === 'email' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
                    data-testid="tab-email"
                  >
                    <MessageSquare className="h-4 w-4 mx-auto mb-1" />
                    Email
                  </button>
                  <button
                    onClick={() => setChannel('copy')}
                    className={`flex-1 py-3 px-4 text-sm font-medium transition-all ${channel === 'copy' ? 'text-white' : 'text-gray-400'}`}
                    style={{ background: channel === 'copy' ? 'rgba(124, 58, 237, 0.3)' : 'transparent' }}
                    data-testid="tab-copy"
                  >
                    <Copy className="h-4 w-4 mx-auto mb-1" />
                    Copy Link
                  </button>
                </div>

                {/* SMS Tab Content */}
                {channel === 'sms' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label style={{ color: '#E5E7EB' }}>Recipient Phone *</Label>
                        {contactPickerSupported && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={pickContact}
                            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10 text-xs px-2 py-1 h-auto"
                            data-testid="pick-contact-btn"
                          >
                            <Users className="h-3 w-3 mr-1" />
                            Contacts
                          </Button>
                        )}
                      </div>
                      <PhoneInput
                        international
                        defaultCountry="IN"
                        value={toPhone}
                        onChange={setToPhone}
                        className="phone-input-dark"
                        data-testid="recipient-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>Your Name (how they'll know you)</Label>
                      <Input
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder="e.g., A friend, Your secret admirer..."
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                        data-testid="sender-name-input"
                      />
                      <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                        Leave blank to send as "Someone"
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>Message *</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your invite message..."
                        rows={3}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                        data-testid="message-textarea"
                      />
                    </div>
                    <div 
                      className="p-3 rounded-lg text-xs flex items-center gap-2"
                      style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
                    >
                      <Shield className="h-4 w-4 shrink-0" style={{ color: '#A78BFA' }} />
                      <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                        Your phone number stays hidden. SMS sent from our system.
                      </span>
                    </div>
                    <Button 
                      onClick={sendMessage} 
                      className="w-full text-white font-medium h-12"
                      style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                      disabled={loading || !toPhone || !message} 
                      data-testid="send-sms-button"
                    >
                      {loading ? 'Sending...' : 'Send Invite via SMS'}
                    </Button>
                  </div>
                )}

                {/* Email Tab Content */}
                {channel === 'email' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>Recipient Email *</Label>
                      <Input
                        type="email"
                        value={toEmail}
                        onChange={(e) => setToEmail(e.target.value)}
                        placeholder="friend@email.com"
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                        data-testid="recipient-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>Your Name (how they'll know you)</Label>
                      <Input
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder="e.g., A friend, Your secret admirer..."
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                        data-testid="sender-name-email-input"
                      />
                      <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                        Leave blank to send as "Someone"
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>Message *</Label>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your invite message..."
                        rows={3}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 resize-none"
                        data-testid="message-textarea-email"
                      />
                    </div>
                    <div 
                      className="p-3 rounded-lg text-xs flex items-center gap-2"
                      style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)' }}
                    >
                      <Shield className="h-4 w-4 shrink-0" style={{ color: '#A78BFA' }} />
                      <span style={{ color: 'rgba(229, 231, 235, 0.7)' }}>
                        Your email stays hidden. Email sent from our system.
                      </span>
                    </div>
                    <Button 
                      onClick={sendMessage} 
                      className="w-full text-white font-medium h-12"
                      style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                      disabled={loading || !toEmail || !message} 
                      data-testid="send-email-button"
                    >
                      {loading ? 'Sending...' : 'Send Invite via Email'}
                    </Button>
                  </div>
                )}

                {/* Copy Link Tab Content */}
                {channel === 'copy' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label style={{ color: '#E5E7EB' }}>How they'll know you</Label>
                      <Input
                        value={senderName}
                        onChange={(e) => setSenderName(e.target.value)}
                        placeholder={user?.username || "Your name"}
                        className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12"
                        data-testid="copy-sender-name-input"
                      />
                      <p className="text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                        Leave blank to use your username "{user?.username}"
                      </p>
                    </div>
                    <div 
                      className="p-4 rounded-lg text-sm"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <p style={{ color: 'rgba(229, 231, 235, 0.8)', whiteSpace: 'pre-line' }}>
                        {getInviteMessage()}
                      </p>
                    </div>
                    <Button 
                      onClick={copyInvite}
                      className="w-full text-white font-medium h-12"
                      style={{ 
                        background: inviteCopied 
                          ? 'rgba(34, 197, 94, 0.3)' 
                          : 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)',
                        border: inviteCopied ? '1px solid rgba(34, 197, 94, 0.5)' : 'none'
                      }}
                      data-testid="copy-invite-button"
                    >
                      {inviteCopied ? (
                        <><Check className="h-4 w-4 mr-2" /> Shared!</>
                      ) : (
                        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? (
                          <><Share2 className="h-4 w-4 mr-2" /> Share Invite</>
                        ) : (
                          <><Copy className="h-4 w-4 mr-2" /> Copy Invite Link</>
                        )
                      )}
                    </Button>
                    <p className="text-center text-xs" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                      Share via any app - WhatsApp, Telegram, iMessage, etc.
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
          
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 md:p-8 pt-20 md:pt-8">
        <div className="text-center max-w-md px-4">
          <div 
            className="w-16 h-16 md:w-20 md:h-20 rounded-full mx-auto mb-4 md:mb-6 flex items-center justify-center"
            style={{ background: 'rgba(124, 58, 237, 0.1)' }}
          >
            <Lock className="h-8 w-8 md:h-10 md:w-10" style={{ color: '#7C3AED' }} />
          </div>
          <h2 className="text-xl md:text-2xl font-bold mb-2 md:mb-3" style={{ color: '#E5E7EB' }}>
            Select a conversation
          </h2>
          <p className="mb-4 md:mb-6 text-sm md:text-base" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
            Choose from your existing conversations or start a new one
          </p>
          <div className="flex items-center justify-center gap-2 text-xs" style={{ color: 'rgba(229, 231, 235, 0.3)' }}>
            <Lock className="h-3 w-3" />
            All messages are end-to-end encrypted
          </div>
          
          {/* Mobile CTA */}
          <Button 
            onClick={() => setSidebarOpen(true)}
            className="md:hidden mt-6 text-white font-medium relative"
            style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
            data-testid="view-chats-button"
          >
            <MessageSquare className="h-4 w-4 mr-2" /> 
            View Chats
            {totalUnread > 0 && (
              <span 
                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center"
              >
                {totalUnread > 99 ? '99+' : totalUnread}
              </span>
            )}
          </Button>
          
          {/* Invite Friend CTA on main page */}
          <Button 
            onClick={() => setShowSendDialog(true)}
            className="mt-4 text-white font-medium"
            variant="outline"
            style={{ 
              background: 'transparent',
              border: '1px solid rgba(124, 58, 237, 0.5)',
              color: '#A78BFA'
            }}
            data-testid="invite-friend-main-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> 
            Invite a Friend
          </Button>

          {/* Friends Button */}
          <Button 
            onClick={() => navigate('/contacts')}
            className="mt-4 text-white font-medium"
            variant="outline"
            style={{ 
              background: 'transparent',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              color: '#22D3EE'
            }}
            data-testid="friends-btn"
          >
            <Users className="h-4 w-4 mr-2" /> 
            Friends
          </Button>

          {/* Friend Requests Section - ALWAYS VISIBLE */}
          <div 
            id="friend-requests-section"
            className="mt-6 w-full max-w-md mx-auto p-4 rounded-xl"
            style={{ 
              background: 'rgba(124, 58, 237, 0.1)',
              border: '1px solid rgba(124, 58, 237, 0.3)'
            }}
            data-testid="friend-requests-section"
          >
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: '#A78BFA' }}>
              <UserPlus className="h-4 w-4" />
              Friend Requests ({friendRequests.length})
            </h3>
            {friendRequests.length > 0 ? (
              <div className="space-y-3">
                {friendRequests.map((request) => (
                  <div 
                    key={request.id}
                    className="p-3 rounded-lg"
                    style={{ background: 'rgba(255, 255, 255, 0.05)' }}
                    data-testid={`friend-request-${request.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        {/* Avatar */}
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)' }}
                        >
                          {(request.sender_username || request.sender_display_name)?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: '#E5E7EB' }}>
                            {request.sender_username || request.sender_display_name}
                          </p>
                          {/* Show how they were invited */}
                          <p className="text-xs flex items-center gap-1" style={{ color: 'rgba(229, 231, 235, 0.5)' }}>
                            {request.invite_channel === 'email' ? (
                              <><Mail className="h-3 w-3" /> Invited via Email</>
                            ) : request.invite_channel === 'sms' ? (
                              <><Phone className="h-3 w-3" /> Invited via SMS</>
                            ) : (
                              <><Link className="h-3 w-3" /> Invited via Link</>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Message preview */}
                    {request.message && (
                      <p className="mt-2 text-xs italic truncate" style={{ color: 'rgba(229, 231, 235, 0.6)' }}>
                        "{request.message}"
                      </p>
                    )}
                    {/* Action buttons */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={() => acceptFriendRequest(request.id)}
                        disabled={friendRequestsLoading}
                        className="flex-1 text-xs"
                        style={{ 
                          background: 'linear-gradient(135deg, #7C3AED 0%, #22D3EE 100%)',
                          color: 'white'
                        }}
                        data-testid={`accept-request-${request.id}`}
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => declineFriendRequest(request.id)}
                        disabled={friendRequestsLoading}
                        className="flex-1 text-xs"
                        style={{ 
                          background: 'transparent',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: 'rgba(229, 231, 235, 0.7)'
                        }}
                        data-testid={`decline-request-${request.id}`}
                      >
                        <X className="h-3 w-3 mr-1" />
                        Decline
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm py-4" style={{ color: 'rgba(229, 231, 235, 0.4)' }}>
                No pending friend requests
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Custom styles for phone input */}
      <style>{`
        .phone-input-dark .PhoneInputInput {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          color: white;
          font-size: 16px;
          height: 48px;
        }
        .phone-input-dark .PhoneInputInput:focus {
          border-color: #7C3AED;
          outline: none;
        }
        .phone-input-dark .PhoneInputInput::placeholder {
          color: rgba(156, 163, 175, 0.5);
        }
        .phone-input-dark .PhoneInputCountrySelect {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }
      `}</style>

      {/* Security Prompt Modal */}
      <SecurityPrompt 
        user={user}
        isOpen={showSecurityPrompt}
        onClose={() => setShowSecurityPrompt(false)}
        onUpdate={fetchUser}
      />

      {/* Incoming Call Popup */}
      {incomingCall && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90">
          <div className="w-full max-w-sm mx-4 p-8 rounded-3xl text-center"
               style={{ background: 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' }}>
            {/* Caller Avatar */}
            <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center animate-pulse">
              {incomingCall.call_type === 'video' ? (
                <Video className="h-12 w-12 text-white" />
              ) : (
                <Phone className="h-12 w-12 text-white" />
              )}
            </div>
            
            {/* Caller Info */}
            <h2 className="text-2xl font-bold text-white mb-2">
              {incomingCall.caller_name || 'Unknown'}
            </h2>
            <p className="text-gray-400 mb-8">
              Incoming {incomingCall.call_type === 'video' ? 'Video' : 'Voice'} Call...
            </p>
            
            {/* Accept/Decline Buttons */}
            <div className="flex justify-center gap-12">
              {/* Decline */}
              <div className="flex flex-col items-center gap-2">
                <Button
                  onClick={declineIncomingCall}
                  className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 shadow-lg shadow-red-600/30"
                >
                  <PhoneOff className="h-8 w-8" />
                </Button>
                <span className="text-sm text-gray-400">Decline</span>
              </div>
              
              {/* Accept */}
              <div className="flex flex-col items-center gap-2">
                <Button
                  onClick={acceptIncomingCall}
                  className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-700 shadow-lg shadow-green-600/30 animate-pulse"
                >
                  <Phone className="h-8 w-8" />
                </Button>
                <span className="text-sm text-gray-400">Accept</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
